import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Loader2, Search, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/landing/Footer';
import Seo from '../../components/Seo';
import { useGetBlogsQuery } from '../../store/api/apiSlice';
import { resolveFileUrl } from '../../utils/fileUrl';
import { blogListJsonLd } from '../../utils/blogSeo';
import { repoPosts, repoPostSlugs } from '../../content/blog';
import type { BlogContentType, BlogListItem } from '../../types';

const CALENDAR_URL =
    'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1X55XupDolgAEHQOxuiyB8bdyB9hO53uywaJpR60NtMsnMHgAoDlVXD-jzYrNA2z-0z0NIbXvf?gv=true';

function formatDate(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Normalised shape so one card renders both repo articles and CMS posts.
interface CardPost {
    slug: string;
    title: string;
    excerpt: string | null;
    coverImageUrl: string | null;
    publishedAt: string | null;
    tags: string[] | null;
    contentType?: BlogContentType;
    /** Repo posts ship their own asset paths and must not be resolved against the API. */
    local?: boolean;
}

function toCardPost(post: BlogListItem): CardPost {
    return {
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        coverImageUrl: post.coverImageUrl,
        publishedAt: post.publishedAt,
        tags: post.tags,
        contentType: post.contentType,
    };
}

function BlogCard({ post }: { post: CardPost }) {
    const cover = post.local ? post.coverImageUrl : resolveFileUrl(post.coverImageUrl);
    return (
        <Link
            to={`/blog/${post.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md"
        >
            <div className="aspect-[16/9] w-full overflow-hidden bg-gray-100">
                {cover ? (
                    <img
                        src={cover}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#FF6B35]/10 to-[#FF6B35]/5">
                        <FileText className="h-10 w-10 text-[#FF6B35]/40" />
                    </div>
                )}
            </div>
            <div className="flex flex-1 flex-col p-5">
                {post.tags && post.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                        {post.tags.slice(0, 3).map((tag) => (
                            <span
                                key={tag}
                                className="inline-flex items-center rounded-full bg-[#FF6B35]/10 px-2.5 py-0.5 text-[11px] font-medium text-[#FF6B35]"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                )}
                <h3 className="mb-2 text-lg font-bold leading-snug text-gray-900 group-hover:text-[#FF6B35]">
                    {post.title}
                </h3>
                {post.excerpt && <p className="mb-4 line-clamp-3 flex-1 text-sm text-gray-600">{post.excerpt}</p>}
                <div className="mt-auto flex items-center justify-between text-xs text-gray-400">
                    <span>{formatDate(post.publishedAt)}</span>
                    {post.contentType === 'document' && (
                        <span className="inline-flex items-center gap-1 font-medium text-gray-500">
                            <FileText className="h-3.5 w-3.5" /> Document
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}

export default function Blog() {
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const params = useMemo(() => {
        const p: { page: number; limit: number; search?: string } = { page, limit: 9 };
        if (search) p.search = search;
        return p;
    }, [page, search]);

    const { data, isLoading, isFetching } = useGetBlogsQuery(params);

    // Repo articles are the prerendered, SEO facing set. They always show first,
    // and any CMS post that reuses one of their slugs is dropped so a slug never
    // renders twice.
    const repoCards = useMemo<CardPost[]>(() => {
        const term = search.trim().toLowerCase();
        return repoPosts
            .filter((p) => {
                if (!term) return true;
                return (
                    p.title.toLowerCase().includes(term) ||
                    p.excerpt.toLowerCase().includes(term) ||
                    p.tags.some((t) => t.toLowerCase().includes(term))
                );
            })
            .map((p) => ({
                slug: p.slug,
                title: p.title,
                excerpt: p.excerpt,
                coverImageUrl: p.coverImageUrl,
                publishedAt: p.date || null,
                tags: p.tags,
                local: true,
            }));
    }, [search]);

    // Only page 1 shows repo posts, otherwise they would repeat on every page.
    const cmsCards = (data?.items ?? []).filter((p) => !repoPostSlugs.has(p.slug)).map(toCardPost);
    const items = page === 1 ? [...repoCards, ...cmsCards] : cmsCards;
    const totalPages = data?.totalPages ?? 1;

    const listJsonLd = useMemo(
        () => blogListJsonLd(repoPosts.map((p) => ({ slug: p.slug, title: p.title, description: p.description }))),
        [],
    );

    const openScheduler = () => window.open(CALENDAR_URL, '_blank', 'noopener,noreferrer');

    const runSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    return (
        <div className="flex min-h-screen flex-col bg-white">
            <Seo
                title="Blog | Background Checks, Hiring and Verification in Nigeria | YourChecka"
                description="Guides and updates on background checks, employee verification, domestic staff screening, hiring and compliance in Nigeria."
                canonicalPath="/blog"
                jsonLd={listJsonLd}
            />
            <Header />
            <main className="flex-1">
                {/* Hero */}
                <section className="w-full bg-[#F5F5F5] px-4 py-8 md:px-6 lg:px-10 md:py-10">
                    <div className="mx-auto max-w-3xl text-center">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#FF6B35]">
                            YourChecka Blog
                        </p>
                        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl">
                            Insights on hiring, verification and trust
                        </h1>
                        <p className="mt-2 text-sm text-gray-600 md:text-base">
                            Guides and updates on background checks, compliance and building a workforce you can rely
                            on.
                        </p>
                        <form onSubmit={runSearch} className="mx-auto mt-5 flex max-w-md items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="search"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search articles"
                                    className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                                />
                            </div>
                            <button
                                type="submit"
                                className="rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-900"
                            >
                                Search
                            </button>
                        </form>
                    </div>
                </section>

                {/* Grid — spans the full page width, gaining columns on wider screens
                    so cards keep a readable size instead of stretching. */}
                <section className="w-full px-4 py-12 md:px-6 lg:px-10 md:py-16">
                    <div className="w-full">
                        {/* Repo articles render instantly, so only spin when nothing is ready. */}
                        {isLoading && items.length === 0 ? (
                            <div className="flex items-center justify-center py-24">
                                <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="py-24 text-center">
                                <FileText className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                                <p className="text-gray-500">
                                    {search ? `No articles matching “${search}”.` : 'No articles published yet.'}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                    {items.map((post) => (
                                        <BlogCard key={post.slug} post={post} />
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="mt-12 flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page <= 1 || isFetching}
                                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            <ChevronLeft className="h-4 w-4" /> Previous
                                        </button>
                                        <span className="text-sm text-gray-500">
                                            Page {page} of {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={page >= totalPages || isFetching}
                                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Next <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>
            </main>
            <Footer onBookMeeting={openScheduler} />
        </div>
    );
}
